import React, { useCallback } from 'react';
import { SerializedPresaveWidget } from 'fym-common/src/db/serializersTypes';
import { PresaveCampaignStatus } from 'fym-shared/src/smartlinks/presave';
import { TypedMemo } from '../../../../../common/types';
import { LoadingView } from '../../../../../common/views/LoadingView';
import { ErrorView } from '../../../../../common/views/ErrorView/ErrorView';
import { WidgetEditionProps } from '../../props';
import { useGetPresaveCampaignQuery } from '../../../../../../redux/api/fymAPI';
import { PresaveWidgetFormContainer } from './PresaveWidgetFormContainer';
import { PresaveWidgetCreateForm } from './PresaveWidgetCreateForm';
import { PresaveWidgetEditForm } from './PresaveWidgetEditForm';
import { PresaveWidgetClosedView } from './PresaveWidgetClosedView';
import { PresaveWidgetCatalogOnlyView } from './PresaveWidgetCatalogOnlyView';

/**
 * The pre-save block in the Creator. Which of the three states it opens in is
 * decided by the campaign behind it: none yet, one still waiting, or one that
 * is over.
 *
 * The block carries only the campaign's uuid, so the campaign is read before
 * anything can be shown — the numeric id the edit call needs comes back with
 * it.
 */
const PresaveWidgetEditionViewComponent: React.FC<WidgetEditionProps<SerializedPresaveWidget>> = ({
  widget,
  accountId,
  smartLinkId,
  saveWidget,
  deleteWidget,
  isLoading,
  error,
}) => {
  // A "Catalog links" block of a song with no pre-save has no campaign to read or edit.
  const catalogOnly = widget !== undefined && widget.data.hasCampaign === false;
  const campaignUuid = catalogOnly ? undefined : widget?.data.campaignUuid;
  const {
    data: campaignResponse,
    isLoading: isCampaignLoading,
    error: campaignError,
    refetch,
  } = useGetPresaveCampaignQuery({ accountId, campaignId: campaignUuid ?? '' }, { skip: campaignUuid === undefined });

  const retry = useCallback(() => {
    refetch().catch(console.error);
  }, [refetch]);

  const onDelete = useCallback(() => {
    deleteWidget();
  }, [deleteWidget]);

  if (widget !== undefined && catalogOnly) {
    return (
      <PresaveWidgetCatalogOnlyView
        accountId={accountId}
        smartLinkId={smartLinkId}
        trackTitle={widget.data.trackTitle}
        isLoading={isLoading}
        onDelete={onDelete}
      />
    );
  }

  if (widget === undefined || campaignUuid === undefined) {
    return (
      <PresaveWidgetCreateForm
        accountId={accountId}
        smartLinkId={smartLinkId}
        saveWidget={saveWidget}
        isLoading={isLoading}
        error={error}
      />
    );
  }

  const campaign = campaignResponse?.campaign;

  if (campaignError) {
    return (
      <PresaveWidgetFormContainer accountId={accountId} smartLinkId={smartLinkId} isLoading={false}>
        <ErrorView error={campaignError} retry={retry} />
      </PresaveWidgetFormContainer>
    );
  }

  if (isCampaignLoading || campaign === undefined) {
    return (
      <PresaveWidgetFormContainer accountId={accountId} smartLinkId={smartLinkId} isLoading>
        <LoadingView />
      </PresaveWidgetFormContainer>
    );
  }

  if (campaign.status !== PresaveCampaignStatus.scheduled) {
    return (
      <PresaveWidgetClosedView
        accountId={accountId}
        smartLinkId={smartLinkId}
        campaign={campaign}
        isLoading={isLoading}
        onDelete={onDelete}
      />
    );
  }

  return (
    <PresaveWidgetEditForm
      accountId={accountId}
      smartLinkId={smartLinkId}
      widget={widget}
      campaign={campaign}
      saveWidget={saveWidget}
      deleteWidget={deleteWidget}
      isLoading={isLoading}
      error={error}
    />
  );
};

export const PresaveWidgetEditionView = TypedMemo(PresaveWidgetEditionViewComponent);
